import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["emailInput", "submitButton", "submitText", "loadingText", "errorMessage", "formSection", "heroWrapper", "lockedButton"]
  static values = { submitUrl: String }

  static emailProviders = {
    'gmail.com':      { name: 'Gmail',    url: 'https://mail.google.com',       color: '#EA4335', icon: 'M' },
    'googlemail.com': { name: 'Gmail',    url: 'https://mail.google.com',       color: '#EA4335', icon: 'M' },
    'gmx.de':         { name: 'GMX',      url: 'https://navigator.gmx.net/mail',color: '#1C449B', icon: 'G' },
    'gmx.net':        { name: 'GMX',      url: 'https://navigator.gmx.net/mail',color: '#1C449B', icon: 'G' },
    'gmx.at':         { name: 'GMX',      url: 'https://navigator.gmx.net/mail',color: '#1C449B', icon: 'G' },
    'web.de':         { name: 'WEB.DE',   url: 'https://web.de/email',          color: '#FFB100', icon: 'W' },
    't-online.de':    { name: 'T-Online',  url: 'https://email.t-online.de',    color: '#E20074', icon: 'T' },
    'outlook.com':    { name: 'Outlook',   url: 'https://outlook.live.com',     color: '#0078D4', icon: 'O' },
    'outlook.de':     { name: 'Outlook',   url: 'https://outlook.live.com',     color: '#0078D4', icon: 'O' },
    'hotmail.com':    { name: 'Outlook',   url: 'https://outlook.live.com',     color: '#0078D4', icon: 'O' },
    'hotmail.de':     { name: 'Outlook',   url: 'https://outlook.live.com',     color: '#0078D4', icon: 'O' },
    'live.com':       { name: 'Outlook',   url: 'https://outlook.live.com',     color: '#0078D4', icon: 'O' },
    'live.de':        { name: 'Outlook',   url: 'https://outlook.live.com',     color: '#0078D4', icon: 'O' },
    'yahoo.com':      { name: 'Yahoo',    url: 'https://mail.yahoo.com',        color: '#6001D2', icon: 'Y' },
    'yahoo.de':       { name: 'Yahoo',    url: 'https://mail.yahoo.com',        color: '#6001D2', icon: 'Y' },
    'freenet.de':     { name: 'Freenet',  url: 'https://email.freenet.de',      color: '#009EE0', icon: 'F' },
    'posteo.de':      { name: 'Posteo',   url: 'https://posteo.de/webmail',     color: '#009900', icon: 'P' },
    'icloud.com':     { name: 'iCloud',   url: 'https://www.icloud.com/mail',   color: '#333333', icon: 'i' },
    'me.com':         { name: 'iCloud',   url: 'https://www.icloud.com/mail',   color: '#333333', icon: 'i' },
  }

  static defaultProviders = ['gmail.com', 'gmx.de', 'web.de', 'outlook.com']

  connect() {
    this.resendCount = 0
  }

  scrollToForm() {
    const element = this.formSectionTarget
    const rect = element.getBoundingClientRect()
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    const navbarOffset = 100 // Approximate navbar + padding height
    const targetY = rect.top + scrollTop - navbarOffset

    window.scrollTo({
      top: targetY,
      behavior: 'smooth'
    })

    // Wait for the scroll to finish before focusing to avoid an instant jump
    setTimeout(() => {
      if (this.hasEmailInputTarget) {
        this.emailInputTarget.focus({ preventScroll: true })
        this.emailInputTarget.classList.add('ring-4', 'ring-ph24_new_blue/20')
        setTimeout(() => {
          this.emailInputTarget.classList.remove('ring-4', 'ring-ph24_new_blue/20')
        }, 1500)
      }
    }, 800)
  }

  toggleDetails(event) {
    const button = event.currentTarget
    const details = button.nextElementSibling
    const icon = button.querySelector('[data-icon="chevron"]')

    if (details.classList.contains('hidden')) {
      details.classList.remove('hidden')
      details.style.maxHeight = '0px'
      details.style.opacity = '0'
      details.style.overflow = 'hidden'
      details.style.transition = 'max-height 0.3s ease, opacity 0.2s ease'
      requestAnimationFrame(() => {
        details.style.maxHeight = `${details.scrollHeight}px`
        details.style.opacity = '1'
      })
      if (icon) icon.style.transform = 'rotate(180deg)'
      button.querySelector('[data-label]').textContent = 'Details ausblenden'
      details.addEventListener('transitionend', () => {
        details.style.maxHeight = ''
        details.style.overflow = ''
      }, { once: true })
    } else {
      details.style.maxHeight = `${details.offsetHeight}px`
      details.style.overflow = 'hidden'
      details.style.transition = 'max-height 0.3s ease, opacity 0.2s ease'
      requestAnimationFrame(() => {
        details.style.maxHeight = '0px'
        details.style.opacity = '0'
      })
      if (icon) icon.style.transform = 'rotate(0deg)'
      button.querySelector('[data-label]').textContent = 'Details & Inhalte ansehen'
      details.addEventListener('transitionend', () => {
        details.classList.add('hidden')
      }, { once: true })
    }
  }

  escapeHTML(str) {
    const div = document.createElement('div')
    div.textContent = str
    return div.innerHTML
  }

  disconnect() {
    if (this.resendTimer) clearTimeout(this.resendTimer)
  }

  async submit(event) {
    event.preventDefault()

    const email = this.emailInputTarget.value.trim()
    if (!email) return

    this.showLoading()
    this.hideError()

    try {
      const response = await fetch(this.submitUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
        },
        body: JSON.stringify({ email })
      })

      if (response.ok) {
        const data = await response.json()
        this.submittedEmail = email
        if (data.status === "already_confirmed") {
          this.showUnlocked(email)
        } else {
          this.showCheckInbox()
        }
      } else {
        let errorMsg = "Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut."
        try {
          const errorData = await response.json()
          if (errorData.error) errorMsg = errorData.error
        } catch (_) {}
        this.showError(errorMsg)
        this.resetLoading()
      }
    } catch (_) {
      this.showError("Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.")
      this.resetLoading()
    }
  }

  showCheckInbox() {
    const providerButtons = this.getProviderButtons()
    const section = this.formSectionTarget

    // Lock height and crossfade to prevent layout shift
    const currentHeight = section.offsetHeight
    section.style.minHeight = `${currentHeight}px`
    section.style.overflow = 'hidden'

    // Fade out current content
    const currentContent = section.firstElementChild || section
    if (section.firstElementChild) {
      currentContent.style.transition = 'opacity 0.15s ease'
      currentContent.style.opacity = '0'
    }

    setTimeout(() => {
      section.innerHTML = `
        <div class="text-center max-w-lg mx-auto" style="opacity: 0; transition: opacity 0.25s ease;">
          <div class="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 ring-2 ring-white/10">
            <svg class="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
            </svg>
          </div>
          <h2 class="text-lg font-bold text-white mb-1">Fast geschafft!</h2>
          <p class="text-sm text-white/80 max-w-md mx-auto mb-4">
            Bestätigungsmail an <strong class="text-white">${this.escapeHTML(this.submittedEmail)}</strong> gesendet — klicken Sie auf den Link.
          </p>
          <div class="flex flex-wrap items-center justify-center gap-2 mb-3">
            ${providerButtons}
          </div>
          <p class="text-xs text-white/50">
            Nicht erhalten? Spam prüfen oder
            <button id="dl-resend-btn" disabled
                    class="text-xs text-white/60 underline decoration-dotted underline-offset-2 hover:text-white/80 disabled:no-underline disabled:cursor-default disabled:hover:text-white/60 transition">
              <span id="dl-resend-label">erneut senden (2:00)</span>
            </button>
          </p>
        </div>
      `

      // Fade in new content
      requestAnimationFrame(() => {
        const inner = section.firstElementChild
        if (inner) inner.style.opacity = '1'
      })

      this.startResendCountdown(120)
    }, 150) // Wait for fade-out to finish
  }

  showUnlocked(email) {
    const hero = this.heroWrapperTarget
    const buttons = [...this.lockedButtonTargets]
    const downloadCount = buttons.length

    // Inject keyframes for the success animation
    if (!document.getElementById('dl-unlock-styles')) {
      const style = document.createElement('style')
      style.id = 'dl-unlock-styles'
      style.textContent = `
        @keyframes dl-checkmark-draw {
          0% { stroke-dashoffset: 24; }
          100% { stroke-dashoffset: 0; }
        }
        @keyframes dl-ring-expand {
          0% { transform: scale(0.8); opacity: 0; }
          50% { transform: scale(1.15); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes dl-btn-unlock {
          0% { transform: scale(1); }
          40% { transform: scale(1.04); }
          100% { transform: scale(1); }
        }
        @keyframes dl-shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `
      document.head.appendChild(style)
    }

    // Phase 1: Fade out form, show success checkmark in hero
    const formContent = hero.querySelector('.relative')
    if (formContent) {
      formContent.style.transition = 'opacity 0.2s ease, filter 0.2s ease'
      formContent.style.opacity = '0'
      formContent.style.filter = 'blur(4px)'
    }

    // Lock hero height for smooth animation
    const startHeight = hero.offsetHeight
    hero.style.height = `${startHeight}px`
    hero.style.overflow = 'hidden'

    setTimeout(() => {
      // Phase 2: Show centered success state briefly
      hero.innerHTML = `
        <div class="absolute inset-0 bg-gradient-to-br from-[#1e2a4a] via-ph24_new_blue to-[#1a2545]"></div>
        <div class="absolute -top-20 -right-20 w-72 h-72 bg-ph24_blue_secondary/20 rounded-full blur-3xl"></div>
        <div class="absolute -bottom-16 -left-16 w-56 h-56 bg-ph24_blue_secondary/15 rounded-full blur-3xl"></div>
        <div class="relative px-6 sm:px-10 py-10 sm:py-12 flex items-center justify-center">
          <div class="text-center" style="animation: dl-ring-expand 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;">
            <div class="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-green-500/20 ring-4 ring-green-400/30">
              <svg class="h-8 w-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"
                      style="stroke-dasharray: 24; stroke-dashoffset: 24; animation: dl-checkmark-draw 0.4s 0.15s ease forwards;" />
              </svg>
            </div>
            <p class="text-lg font-bold text-white">Downloads freigeschaltet!</p>
          </div>
        </div>
      `

      // Phase 3: After success flash, transition to compact header
      setTimeout(() => {
        const successContent = hero.querySelector('.relative')
        if (successContent) {
          successContent.style.transition = 'opacity 0.2s ease'
          successContent.style.opacity = '0'
        }

        setTimeout(() => {
          hero.innerHTML = `
            <div class="absolute inset-0 bg-gradient-to-br from-ph24_new_blue via-ph24_new_blue to-[#1a2545]"></div>
            <div class="absolute -top-16 -right-16 w-48 h-48 bg-ph24_blue_secondary/20 rounded-full blur-3xl"></div>
            <div class="absolute -bottom-8 left-1/4 w-32 h-32 bg-ph24_blue_secondary/10 rounded-full blur-2xl"></div>
            <div class="relative px-6 sm:px-10 py-6" style="opacity: 0; transition: opacity 0.3s ease;">
              <div class="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div class="flex items-center gap-4">
                  <div class="flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 backdrop-blur-sm ring-1 ring-white/20">
                    <svg class="h-5 w-5 text-green-300" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 10.5V6.75a4.5 4.5 0 119 0v3.75M3.75 21.75h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H3.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                    </svg>
                  </div>
                  <div>
                    <h1 class="text-xl font-bold text-white font-atkinson">Pflege-Ratgeber & Vorlagen</h1>
                    <p class="text-sm text-white/60">Freigeschaltet für <span class="text-white/90 font-medium">${this.escapeHTML(email)}</span></p>
                  </div>
                </div>
                <div class="text-xs font-medium text-white/50 bg-white/10 px-3 py-1 rounded-full border border-white/10">
                  ${downloadCount} Downloads verfügbar
                </div>
              </div>
            </div>
          `
          // Fade in compact header
          requestAnimationFrame(() => {
            const newInner = hero.querySelector('.relative')
            if (newInner) newInner.style.opacity = '1'
          })

          // Measure target height properly: unlock, measure, re-lock, animate
          setTimeout(() => {
            hero.style.transition = 'none'
            hero.style.height = 'auto'
            const targetHeight = hero.offsetHeight
            hero.style.height = `${startHeight}px`
            // Force reflow so browser registers the locked height before animating
            hero.offsetHeight
            hero.style.transition = 'height 1.2s cubic-bezier(0.22, 1, 0.36, 1)'
            hero.style.height = `${targetHeight}px`
            hero.addEventListener('transitionend', () => {
              hero.style.height = ''
              hero.style.overflow = ''
              hero.style.transition = ''
            }, { once: true })
          }, 600)
        }, 200)
      }, 800)
    }, 250)

    // Phase 4: Staggered button unlock cascade (starts after success flash)
    setTimeout(() => {
      buttons.forEach((btn, i) => {
        setTimeout(() => {
          const url = btn.dataset.assetUrl
          const wrapper = btn.parentElement

          // Brief pulse on the card
          const card = btn.closest('.group')
          if (card) {
            card.style.transition = 'box-shadow 0.3s ease, transform 0.3s ease'
            card.style.boxShadow = '0 0 0 2px rgba(36, 49, 93, 0.3), 0 8px 25px -5px rgba(36, 49, 93, 0.15)'
            card.style.transform = 'translateY(-2px)'
            setTimeout(() => {
              card.style.boxShadow = ''
              card.style.transform = ''
            }, 400)
          }

          // Animate button swap
          btn.style.transition = 'transform 0.2s ease, opacity 0.2s ease'
          btn.style.transform = 'scale(0.95)'
          btn.style.opacity = '0'

          setTimeout(() => {
            const link = document.createElement('a')
            link.href = url
            link.target = '_blank'
            link.rel = 'noopener noreferrer'
            link.className = 'inline-flex items-center justify-center gap-3 w-full rounded-xl bg-ph24_new_blue px-6 py-3.5 text-base text-white font-bold shadow-lg shadow-ph24_new_blue/20 hover:bg-ph24_new_blue/90 hover:shadow-xl hover:shadow-ph24_new_blue/30 transform transition-all duration-200'
            link.style.transform = 'scale(0.95)'
            link.style.opacity = '0'
            link.innerHTML = `
              <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
              </svg>
              Jetzt herunterladen
            `
            btn.replaceWith(link)
            requestAnimationFrame(() => {
              link.style.transition = 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.3s ease'
              link.style.transform = 'scale(1)'
              link.style.opacity = '1'
            })
          }, 200)
        }, i * 80) // 80ms stagger per card
      })
    }, 600)
  }

  startResendCountdown(seconds) {
    const btn = document.getElementById('dl-resend-btn')
    const label = document.getElementById('dl-resend-label')
    if (!btn || !label) return

    btn.disabled = true
    let remaining = seconds

    const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`

    const tick = () => {
      if (remaining <= 0) {
        label.textContent = 'erneut senden'
        btn.disabled = false
        btn.onclick = () => this.resendEmail()
        return
      }
      label.textContent = `erneut senden (${formatTime(remaining)})`
      remaining--
      this.resendTimer = setTimeout(tick, 1000)
    }
    tick()
  }

  async resendEmail() {
    const btn = document.getElementById('dl-resend-btn')
    const label = document.getElementById('dl-resend-label')
    if (!btn || !label) return

    btn.disabled = true
    label.textContent = 'Wird gesendet...'

    let ok = false
    try {
      const response = await fetch(this.submitUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
        },
        body: JSON.stringify({ email: this.submittedEmail })
      })
      ok = response.ok
    } catch (_) {}

    if (!ok) {
      label.textContent = 'erneut senden'
      btn.disabled = false
      return
    }

    this.resendCount++
    if (this.resendCount >= 3) {
      label.textContent = 'erneut gesendet ✓'
      btn.disabled = true
      btn.classList.remove('underline', 'decoration-dotted')
      return
    }

    label.textContent = 'erneut gesendet ✓'
    setTimeout(() => this.startResendCountdown(60), 1500)
  }

  showLoading() {
    const btn = this.submitButtonTarget
    btn.style.minWidth = `${btn.offsetWidth}px`
    btn.disabled = true
    this.submitTextTarget.classList.add("hidden")
    this.loadingTextTarget.classList.remove("hidden")
  }

  resetLoading() {
    this.submitButtonTarget.style.minWidth = ''
    this.submitButtonTarget.disabled = false
    this.submitTextTarget.classList.remove("hidden")
    this.loadingTextTarget.classList.add("hidden")
  }

  showError(message) {
    this.errorMessageTarget.textContent = message
    this.errorMessageTarget.classList.remove("hidden")
  }

  hideError() {
    this.errorMessageTarget.classList.add("hidden")
  }

  getProviderButtons() {
    const domain = (this.submittedEmail || "").split("@")[1]?.toLowerCase()
    const providers = this.constructor.emailProviders
    const matched = providers[domain]

    if (matched) {
      return this.providerButtonHTML(matched, true)
    }

    return this.constructor.defaultProviders.map(key =>
      this.providerButtonHTML(providers[key], false)
    ).join('') + `<span class="text-[11px] text-white/30 block mt-1">Oder öffnen Sie Ihr E-Mail-Programm direkt</span>`
  }

  providerButtonHTML(provider, primary) {
    if (primary) {
      return `
        <a href="${provider.url}" target="_blank" rel="noopener noreferrer"
           class="inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2 text-sm font-semibold text-white transition hover:opacity-90"
           style="background-color: ${provider.color};">
          <span class="flex h-5 w-5 items-center justify-center rounded text-[11px] font-bold bg-white/20">${provider.icon}</span>
          ${provider.name} öffnen
        </a>
      `
    }
    return `
      <a href="${provider.url}" target="_blank" rel="noopener noreferrer"
         class="inline-flex items-center gap-1.5 rounded-md border border-white/20 px-3 py-1.5 text-xs font-medium text-white/80 transition hover:bg-white/10 hover:text-white">
        <span class="flex h-4 w-4 items-center justify-center rounded text-[9px] font-bold text-white" style="background-color: ${provider.color};">${provider.icon}</span>
        ${provider.name}
      </a>
    `
  }
}
